import * as Sentry from "@sentry/browser";
import { AdministratorSimple, IdentityService } from "company-dashboard/services/identity";
import { Router } from "aurelia-router";
import { autoinject, bindable } from "aurelia-framework";
import { IValidation, ModalService, ToastService } from "shared/framework";
import { BaseModule } from "shared/framework/components/base-module/base-module";
import { ApiValidationError, GoogleTagManagerService } from "shared/infrastructure";
import { Operation } from "shared/utilities";
import { AdministratorExistsService } from "company-dashboard/model/sign-up/administrator-exists";
import { AdministratorAlreadyExists } from "./modals/administrator-already-exists/administrator-already-exists";
import { SignUpCompanyService, Administrator, Company, CompanyTypeahead } from "company-dashboard/model/sign-up/sign-up-company";

/**
 * Represents the module.
 */
@autoinject
export class SignUpCompanyModule extends BaseModule
{
	public constructor(
		toastService: ToastService,
		modalService: ModalService,
		identityService: IdentityService,
		signUpCompanyService: SignUpCompanyService,
		administratorExistsService: AdministratorExistsService,
		googleTagManagerService: GoogleTagManagerService,
		router: Router)
	{
		super();

		this._toastService = toastService;
		this._modalService = modalService;
		this._identityService = identityService;
		this._identityService.unauthenticate();
		this._signUpCompanyService = signUpCompanyService;
		this._administratorExistsService = administratorExistsService;
		this._googleTagManagerService = googleTagManagerService;
		this._router = router;
	}

	private readonly _toastService: ToastService;
	private readonly _modalService: ModalService;
	private readonly _identityService: IdentityService;
	private readonly _signUpCompanyService: SignUpCompanyService;
	private readonly _administratorExistsService: AdministratorExistsService
	private readonly _googleTagManagerService: GoogleTagManagerService;
	private readonly _router: Router;

	protected administratorInformationValidation: IValidation;

	protected companyInformationValidation: IValidation;

	protected companySearchOperation: Operation;

	protected currentForm: "administrator-information" | "company-information" = "administrator-information";

	protected passwordVisible: boolean = false;

	protected administrator: Administrator = new Administrator();

	protected company: Company = new Company();

  @bindable
	protected companySelected: CompanyTypeahead | undefined;

	public doActivate(): void
	{}

	protected warnProductionGrandhoodEmployee(): void
	{
		if (ENVIRONMENT.name === "production" && this.administrator.email.includes("@grandhood.dk"))
		{
			alert("ER DU SIKKER PÅ AT DU GERNE VIL BRUGE DEN EMAIL I PRODUKTION?");
		}
	}

	/**
	 * Check if the password is 8 characters long
	 * contains atleast a number, and letters
	 */
	protected get passwordValid(): boolean
	{
		if (this.administrator.password == null)
		{
			return false;
		}

		if (this.administrator.password.length < 8)
		{
			return false;
		}

		if (!/\d/.test(this.administrator.password))
		{
			return false;
		}

		if (!isNaN(Number(this.administrator.password)))
		{
			return false;
		}

		return true;
	}

	protected onSearchInputFocus(lookupMenu: any): void
	{
		lookupMenu.open = true;
	}

	protected async onContinueToCompanyInformationClick(): Promise<void>
	{
		this.administratorInformationValidation.active = true;

		if (!await this.administratorInformationValidation.validate())
		{
			return;
		}

		if (this.updateOperation != null)
		{
			this.updateOperation.abort();
		}

		this.updateOperation = new Operation(async signal =>
		{
			try
			{
				const exists = await this._administratorExistsService.exists(this.administrator.email, signal);
				if(exists)
				{
					await this._modalService.open(AdministratorAlreadyExists, { email: this.administrator.email }).promise;
				}
				else
				{
					this._identityService.identity.administrator = new AdministratorSimple({ ...this.administrator })

					this._googleTagManagerService.pushEvent("sign_up_step_administrator_info", { signup_step: 1 });

					this.currentForm = "company-information";
				}
			}
			catch(error: any)
			{
				this._toastService.open(
					"error",
					{
						"message": "Vi har tekniske problemer lige nu. Skriv på support@grandhood.dk hvis problemet bliver ved."
					}
				);
			}
		});
	}

	protected async onSignUpCompany(): Promise<void>
	{
		this.companyInformationValidation.active = true;

		if (!await this.companyInformationValidation.validate())
		{
			return;
		}

		if (this.updateOperation != null)
		{
			this.updateOperation.abort();
		}

		// Create and execute the operation
		this.updateOperation = new Operation(async signal =>
		{
			try
			{
				await this._signUpCompanyService.signUpCompany(this.company.mergeQueriedCompany(this.companySelected!), this.administrator, signal);

				await this._identityService.initial();

				this._googleTagManagerService.pushEvent("sign_up_step_company_info", { signup_step: 2 });

				this._router.navigate("aftaledokumenter", { replace: true });
			}
			catch (error: any)
			{
				if(error instanceof ApiValidationError)
				{
					if(error.data.title == "Company structure is unsupported.")
					{
						this._toastService.open(
							"error",
							{
								"message": "Vi understøtter desværre ikke din virksomhedsstruktur. Kontakt os gerne for mere information."
							}
						);
					}
					else if(error.data.title == "Company branch is unsupported.")
					{
						this._toastService.open(
							"error",
							{
								"message": "Vi understøtter desværre ikke branchen som din virksomhed er i. Kontakt os gerne for mere information."
							}
						);
					}
					else if(error.data.title == "Company already exists with provided CVR number.")
					{
						this._toastService.open(
							"error",
							{
								"message": "Din virksomhed er allerede i vores system. Har du eller en kollega tidligere være forbi os? Kontakt os gerne for mere information."
							}
						);
					}
					else if(error.data.title == "One or more validation errors occurred.")
					{
						// The error is not expected as the Frontend should handle
						// all validation errors regarding the input provided.
						// This is a catch to ensure a friendly error message to
						// the user, but the error is logged as to notify that
						// a change needs to be made in the Frontend.
						Sentry.captureException(error);

						this._toastService.open(
							"error",
							{
								"message": "En eller flere af felterne mangler at blive udfyldt."
							}
						);
					}
					else
					{
						// The error is not expected, but was not seen as
						// an error from the Backend. So the error is
						// logged to Sentry from the Frontend.
						Sentry.captureException(error);

						this._toastService.open(
							"error",
							{
								"message": "Vi har tekniske problemer lige nu. Skriv på support@grandhood.dk hvis problemet bliver ved."
							}
						);
					}
				}
				else
				{
					if ([401, 403].includes(error.response.status))
					{
						this._toastService.open(
							"error",
							{
								"message": "Vi kunne ikke genkende mail eller adgangskode. Har du glemt din adgangskode?"
							}
						);
					}
					else
					{
						this._toastService.open(
							"error",
							{
								"message": "Vi har tekniske problemer lige nu. Skriv på support@grandhood.dk hvis problemet bliver ved."
							}
						)
					}
					console.log("An error occurred.\n", error, signal);
				}
			}
		})
	}
}
